import React, { useCallback, useMemo } from 'react';
import { BaseEdge, useReactFlow } from '@xyflow/react';

const WAYPOINT_GRID = 10;
const STUB_LENGTH = 20;

const getOutwardDirection = (position) => {
  switch (position) {
    case 'top':
      return { x: 0, y: -1 };
    case 'right':
      return { x: 1, y: 0 };
    case 'bottom':
      return { x: 0, y: 1 };
    case 'left':
      return { x: -1, y: 0 };
    default:
      return { x: 0, y: 0 };
  }
};
const cleanupRedundantWaypoints = (
  waypoints,
  sourceX,
  sourceY,
  sourceStub,
  targetStub,
  targetX,
  targetY
) => {
  let current = [...waypoints];
  let changed = true;

  while (changed) {
    changed = false;
    const P = [
      { x: sourceX, y: sourceY },
      sourceStub,
      ...current,
      targetStub,
      { x: targetX, y: targetY },
    ];

    for (let k = 0; k < current.length; k++) {
      const pPrev = P[k + 1];
      const pCurr = P[k + 2];
      const pNext = P[k + 3];

      // Same Y (or X) alone isn't enough - pCurr must also sit BETWEEN
      // pPrev and pNext, not be a zigzag/reversal point (e.g. path goes
      // right then back left along the same Y). Removing a reversal point
      // changes the path, so only a genuine straight-line pass-through
      // (monotonic) is safe to drop.
      const isCollinearH =
        Math.abs(pPrev.y - pCurr.y) < 1 &&
        Math.abs(pCurr.y - pNext.y) < 1 &&
        (pCurr.x - pPrev.x) * (pNext.x - pCurr.x) >= 0;
      const isCollinearV =
        Math.abs(pPrev.x - pCurr.x) < 1 &&
        Math.abs(pCurr.x - pNext.x) < 1 &&
        (pCurr.y - pPrev.y) * (pNext.y - pCurr.y) >= 0;
      const isSameAsNeighbor =
        (Math.abs(pPrev.x - pCurr.x) < 1 && Math.abs(pPrev.y - pCurr.y) < 1) ||
        (Math.abs(pNext.x - pCurr.x) < 1 && Math.abs(pNext.y - pCurr.y) < 1);

      if (isCollinearH || isCollinearV || isSameAsNeighbor) {
        current.splice(k, 1);
        changed = true;
        break;
      }
    }
  }

  return current;
};

export default function WaypointEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
  markerEnd,
  style,
}) {
  const { setEdges, screenToFlowPosition } = useReactFlow();
  const waypoints = useMemo(() => data?.waypoints || [], [data?.waypoints]);
  const activeMode = data?.activeMode || 'edit';
  const readOnly = activeMode !== 'edit';
  const isPreview = data?.isPreview || false;

  const sourceDir = useMemo(() => getOutwardDirection(sourcePosition), [sourcePosition]);
  const targetDir = useMemo(() => getOutwardDirection(targetPosition), [targetPosition]);

  const sourceStubLength = useMemo(() => {
    if (sourceDir.x !== 0 && targetDir.x !== 0) {
      if (sourceDir.x * targetDir.x < 0) {
        const gap = (targetX - sourceX) * sourceDir.x;
        if (gap > 0) {
          return Math.max(5, Math.min(STUB_LENGTH, gap / 2));
        }
      }
    }
    if (sourceDir.y !== 0 && targetDir.y !== 0) {
      if (sourceDir.y * targetDir.y < 0) {
        const gap = (targetY - sourceY) * sourceDir.y;
        if (gap > 0) {
          return Math.max(5, Math.min(STUB_LENGTH, gap / 2));
        }
      }
    }
    return STUB_LENGTH;
  }, [sourceX, sourceY, sourceDir.x, sourceDir.y, targetX, targetY, targetDir.x, targetDir.y]);

  const targetStubLength = sourceStubLength;

  const effectiveSourceStubLength = useMemo(() => {
    if (sourcePosition === 'top') {
      return Math.min(sourceStubLength, Math.max(2, sourceY));
    }
    return sourceStubLength;
  }, [sourcePosition, sourceStubLength, sourceY]);

  const effectiveTargetStubLength = useMemo(() => {
    if (targetPosition === 'top') {
      return Math.min(targetStubLength, Math.max(2, targetY));
    }
    return targetStubLength;
  }, [targetPosition, targetStubLength, targetY]);

  const sourceStub = useMemo(
    () => ({
      x: sourceX + sourceDir.x * effectiveSourceStubLength,
      y: Math.max(0, sourceY + sourceDir.y * effectiveSourceStubLength),
    }),
    [sourceX, sourceY, sourceDir.x, sourceDir.y, effectiveSourceStubLength]
  );

  const targetStub = useMemo(
    () => ({
      x: targetX + targetDir.x * effectiveTargetStubLength,
      y: Math.max(0, targetY + targetDir.y * effectiveTargetStubLength),
    }),
    [targetX, targetY, targetDir.x, targetDir.y, effectiveTargetStubLength]
  );

  // Builds the rendered polyline (source -> waypoints -> target, with a
  // right-angle dogleg inserted wherever two consecutive points aren't
  // already aligned). Every entry is tagged with `waypointIndex`: the
  // point's real position in the `waypoints` array, or -1 if it's a
  // synthetic point (source/target, a stub, or an auto-inserted dogleg
  // corner). This tag is the single source of truth for "which stored
  // waypoint is this on-screen point" - drag handlers key off it directly
  // instead of comparing x/y coordinates, which breaks down whenever two
  // unrelated points land on the same grid coordinate (common in a
  // grid-aligned staircase layout).
  const { pathString, fullPoints } = useMemo(() => {
    const P = [
      { x: sourceX, y: sourceY },
      sourceStub,
      ...waypoints,
      targetStub,
      { x: targetX, y: targetY },
    ];

    const waypointIndexForPIndex = (pIndex) => {
      const wpIdx = pIndex - 2;
      return wpIdx >= 0 && wpIdx < waypoints.length ? wpIdx : -1;
    };

    const pts = [{ ...P[0], parentSegmentIndex: 0, waypointIndex: waypointIndexForPIndex(0) }];

    // Tracks how we arrived at the last pushed point: true if the leg into
    // it ran horizontally, false if vertically. Used to pick the bend
    // direction for interior segments (see below).
    let arrivedHorizontally = null;

    for (let i = 0; i < P.length - 1; i++) {
      const p1 = P[i];
      const p2 = P[i + 1];

      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;

      if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) {
        continue;
      }

      const wpIdxForP2 = waypointIndexForPIndex(i + 1);

      if (Math.abs(dx) < 0.01) {
        // Purely vertical
        pts.push({ x: p2.x, y: p2.y, parentSegmentIndex: i, waypointIndex: wpIdxForP2 });
        arrivedHorizontally = false;
      } else if (Math.abs(dy) < 0.01) {
        // Purely horizontal
        pts.push({ x: p2.x, y: p2.y, parentSegmentIndex: i, waypointIndex: wpIdxForP2 });
        arrivedHorizontally = true;
      } else {
        // Diagonal: pick horizontal-first vs vertical-first for the bend.
        let segmentGoHorizontalFirst;
        if (i === 1) {
          // Leaving the source node: keep the original exit-direction
          // heuristic so edges continue naturally outward from the node
          // before bending.
          if (sourcePosition === 'left') {
            segmentGoHorizontalFirst = dx <= 0;
          } else if (sourcePosition === 'right') {
            segmentGoHorizontalFirst = dx >= 0;
          } else if (sourcePosition === 'top') {
            segmentGoHorizontalFirst = dy > 0;
          } else if (sourcePosition === 'bottom') {
            segmentGoHorizontalFirst = dy < 0;
          } else {
            segmentGoHorizontalFirst = true;
          }
        } else {
          // Interior bend: turn the OTHER way from however we arrived at
          // p1, so the path always makes one clean turn instead of doubling
          // back on the same axis. This keeps the bend shape identical
          // whether the next point is above/below or left/right of p1 -
          // dragging a segment up looks the same as dragging it down, just
          // mirrored.
          segmentGoHorizontalFirst = !arrivedHorizontally;
        }

        if (segmentGoHorizontalFirst) {
          pts.push({
            x: p2.x,
            y: p1.y,
            parentSegmentIndex: i,
            isAutoCorner: true,
            waypointIndex: -1,
          });
          pts.push({ x: p2.x, y: p2.y, parentSegmentIndex: i, waypointIndex: wpIdxForP2 });
        } else {
          pts.push({
            x: p1.x,
            y: p2.y,
            parentSegmentIndex: i,
            isAutoCorner: true,
            waypointIndex: -1,
          });
          pts.push({ x: p2.x, y: p2.y, parentSegmentIndex: i, waypointIndex: wpIdxForP2 });
        }
        arrivedHorizontally = !segmentGoHorizontalFirst;
      }
    }

    const pStr = pts.map((p, index) => `${index === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    return { pathString: pStr, fullPoints: pts };
  }, [waypoints, sourceX, sourceY, sourcePosition, targetX, targetY, sourceStub, targetStub]);

  const setEdgesToUse = data?.setEdges || setEdges;

  // Rebuilds the waypoints array from fullPoints, in path order: every point
  // that is already real (waypointIndex >= 0) OR whose fullPoints index is
  // listed in `promoteFpIndices` becomes an entry in the result, using the
  // coordinates in `overrideByFpIndex` when given. Because this walks
  // fullPoints in order, the returned array is always correctly ordered, and
  // `fpIdxToNewIndex` gives the exact final array index for any fpIdx
  // involved - no coordinate matching required.
  const rebuildWaypoints = useCallback(
    (promoteFpIndices, overrideByFpIndex) => {
      const result = [];
      const fpIdxToNewIndex = new Map();
      for (let fpIdx = 0; fpIdx < fullPoints.length; fpIdx++) {
        const fp = fullPoints[fpIdx];
        const isReal = fp.waypointIndex >= 0 || promoteFpIndices.has(fpIdx);
        if (!isReal) continue;
        const override = overrideByFpIndex && overrideByFpIndex.get(fpIdx);
        fpIdxToNewIndex.set(fpIdx, result.length);
        result.push(override ? { x: override.x, y: override.y } : { x: fp.x, y: fp.y });
      }
      return { waypoints: result, fpIdxToNewIndex };
    },
    [fullPoints]
  );

  // Which fullPoints neighbors (just outside fpIdx) are still unpinned
  // auto-corners that should be promoted to real waypoints alongside it, so
  // the auto-router doesn't reshape the rest of the path once fpIdx moves.
  const outerAutoCornerNeighbors = useCallback(
    (fpIdx) => {
      const result = [];
      const before = fullPoints[fpIdx - 1];
      const after = fullPoints[fpIdx + 1];
      if (fpIdx - 1 > 0 && before && before.waypointIndex < 0) result.push(fpIdx - 1);
      if (fpIdx + 1 < fullPoints.length - 1 && after && after.waypointIndex < 0) {
        result.push(fpIdx + 1);
      }
      return result;
    },
    [fullPoints]
  );

  // Update Waypoint Drag Position
  const updateWaypointPosition = useCallback(
    (k, targetX, targetY) => {
      setEdgesToUse((eds) =>
        eds.map((edge) => {
          if (edge.id !== id) return edge;

          const currentWaypoints = edge.data?.waypoints ? [...edge.data.waypoints] : [];
          if (!currentWaypoints[k]) return edge;

          currentWaypoints[k] = { x: targetX, y: targetY };

          return {
            ...edge,
            data: {
              ...edge.data,
              waypoints: currentWaypoints,
            },
          };
        })
      );
    },
    [id, setEdgesToUse]
  );

  const onWaypointMouseDown = (e, index) => {
    e.stopPropagation();
    e.preventDefault();

    const fpIdx = fullPoints.findIndex((fp) => fp.waypointIndex === index);
    let workingIndex = index;

    if (fpIdx !== -1) {
      const promote = new Set(outerAutoCornerNeighbors(fpIdx));
      if (promote.size > 0) {
        const { waypoints: rebuilt, fpIdxToNewIndex } = rebuildWaypoints(promote, null);
        workingIndex = fpIdxToNewIndex.get(fpIdx);

        setEdgesToUse((eds) =>
          eds.map((edge) =>
            edge.id === id ? { ...edge, data: { ...edge.data, waypoints: rebuilt } } : edge
          )
        );
      }
    }

    const handleMouseMove = (moveEvent) => {
      const flowPos = screenToFlowPosition({
        x: moveEvent.clientX,
        y: moveEvent.clientY,
      });

      let draggedX = Math.round(flowPos.x / WAYPOINT_GRID) * WAYPOINT_GRID;
      let draggedY = Math.round(flowPos.y / WAYPOINT_GRID) * WAYPOINT_GRID;

      const snapThreshold = 10;
      // Snap X
      if (Math.abs(flowPos.x - sourceX) < snapThreshold) {
        draggedX = sourceX;
      } else if (Math.abs(flowPos.x - targetX) < snapThreshold) {
        draggedX = targetX;
      } else if (Math.abs(flowPos.x - sourceStub.x) < snapThreshold) {
        draggedX = sourceStub.x;
      } else if (Math.abs(flowPos.x - targetStub.x) < snapThreshold) {
        draggedX = targetStub.x;
      } else {
        for (const wp of waypoints) {
          if (Math.abs(flowPos.x - wp.x) < snapThreshold) {
            draggedX = wp.x;
            break;
          }
        }
      }

      // Snap Y
      if (Math.abs(flowPos.y - sourceY) < snapThreshold) {
        draggedY = sourceY;
      } else if (Math.abs(flowPos.y - targetY) < snapThreshold) {
        draggedY = targetY;
      } else if (Math.abs(flowPos.y - sourceStub.y) < snapThreshold) {
        draggedY = sourceStub.y;
      } else if (Math.abs(flowPos.y - targetStub.y) < snapThreshold) {
        draggedY = targetStub.y;
      } else {
        for (const wp of waypoints) {
          if (Math.abs(flowPos.y - wp.y) < snapThreshold) {
            draggedY = wp.y;
            break;
          }
        }
      }

      updateWaypointPosition(workingIndex, draggedX, draggedY);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      setEdgesToUse((eds) =>
        eds.map((edge) => {
          if (edge.id !== id) return edge;
          const currentWaypoints = edge.data?.waypoints ? [...edge.data.waypoints] : [];
          const cleaned = cleanupRedundantWaypoints(
            currentWaypoints,
            sourceX,
            sourceY,
            sourceStub,
            targetStub,
            targetX,
            targetY
          );
          return {
            ...edge,
            data: {
              ...edge.data,
              waypoints: cleaned,
            },
          };
        })
      );

      window.dispatchEvent(new CustomEvent('unsavedChanges'));
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const onWaypointDoubleClick = (e, index) => {
    e.stopPropagation();
    e.preventDefault();

    setEdgesToUse((eds) =>
      eds.map((edge) => {
        if (edge.id !== id) return edge;
        const currentWaypoints = edge.data?.waypoints ? [...edge.data.waypoints] : [];
        currentWaypoints.splice(index, 1);
        return {
          ...edge,
          data: {
            ...edge.data,
            waypoints: currentWaypoints,
          },
        };
      })
    );

    window.dispatchEvent(new CustomEvent('unsavedChanges'));
  };

  const onSegmentDoubleClick = (e, idx) => {
    e.stopPropagation();
    e.preventDefault();

    if (!fullPoints[idx + 1]) return;

    const flowPos = screenToFlowPosition({
      x: e.clientX,
      y: e.clientY,
    });
    const newX = Math.round(flowPos.x / WAYPOINT_GRID) * WAYPOINT_GRID;
    const newY = Math.round(flowPos.y / WAYPOINT_GRID) * WAYPOINT_GRID;

    // Insert the new point right after however many real waypoints already
    // precede this segment along the path.
    let insertIndex = 0;
    for (let j = 0; j <= idx; j++) {
      if (fullPoints[j].waypointIndex >= 0) insertIndex++;
    }

    setEdgesToUse((eds) =>
      eds.map((edge) => {
        if (edge.id !== id) return edge;
        const currentWaypoints = edge.data?.waypoints ? [...edge.data.waypoints] : [];
        currentWaypoints.splice(insertIndex, 0, { x: newX, y: newY });
        return {
          ...edge,
          data: {
            ...edge.data,
            waypoints: currentWaypoints,
          },
        };
      })
    );

    window.dispatchEvent(new CustomEvent('unsavedChanges'));
  };

  const onSegmentMouseDown = (e, idx) => {
    e.stopPropagation();
    e.preventDefault();

    const p1 = fullPoints[idx];
    const p2 = fullPoints[idx + 1];
    if (!p1 || !p2) return;

    const isHorizontal = Math.abs(p1.y - p2.y) < 0.01;

    const promote = new Set();
    if (p1.waypointIndex < 0 && idx > 0) promote.add(idx);
    if (p2.waypointIndex < 0 && idx + 1 < fullPoints.length - 1) promote.add(idx + 1);
    // Pin the points just outside this segment so the auto-router doesn't
    // reshape the rest of the path once p1/p2 start moving.
    outerAutoCornerNeighbors(idx).forEach((fpIdx) => promote.add(fpIdx));
    outerAutoCornerNeighbors(idx + 1).forEach((fpIdx) => promote.add(fpIdx));

    const { waypoints: rebuilt, fpIdxToNewIndex } = rebuildWaypoints(promote, null);
    const wp1Index = fpIdxToNewIndex.get(idx);
    const wp2Index = fpIdxToNewIndex.get(idx + 1);

    setEdgesToUse((eds) =>
      eds.map((edge) =>
        edge.id === id ? { ...edge, data: { ...edge.data, waypoints: rebuilt } } : edge
      )
    );

    window.dispatchEvent(new CustomEvent('unsavedChanges'));

    const handleMouseMove = (moveEvent) => {
      const flowPos = screenToFlowPosition({
        x: moveEvent.clientX,
        y: moveEvent.clientY,
      });

      let draggedX = Math.round(flowPos.x / WAYPOINT_GRID) * WAYPOINT_GRID;
      let draggedY = Math.round(flowPos.y / WAYPOINT_GRID) * WAYPOINT_GRID;

      const snapThreshold = 10;
      // Snap X
      if (Math.abs(flowPos.x - sourceX) < snapThreshold) {
        draggedX = sourceX;
      } else if (Math.abs(flowPos.x - targetX) < snapThreshold) {
        draggedX = targetX;
      } else if (Math.abs(flowPos.x - sourceStub.x) < snapThreshold) {
        draggedX = sourceStub.x;
      } else if (Math.abs(flowPos.x - targetStub.x) < snapThreshold) {
        draggedX = targetStub.x;
      }

      // Snap Y
      if (Math.abs(flowPos.y - sourceY) < snapThreshold) {
        draggedY = sourceY;
      } else if (Math.abs(flowPos.y - targetY) < snapThreshold) {
        draggedY = targetY;
      } else if (Math.abs(flowPos.y - sourceStub.y) < snapThreshold) {
        draggedY = sourceStub.y;
      } else if (Math.abs(flowPos.y - targetStub.y) < snapThreshold) {
        draggedY = targetStub.y;
      }

      setEdgesToUse((eds) =>
        eds.map((edge) => {
          if (edge.id !== id) return edge;
          const currentWaypoints = edge.data?.waypoints ? [...edge.data.waypoints] : [];

          if (wp1Index !== undefined && currentWaypoints[wp1Index]) {
            if (isHorizontal) {
              currentWaypoints[wp1Index] = { ...currentWaypoints[wp1Index], y: draggedY };
            } else {
              currentWaypoints[wp1Index] = { ...currentWaypoints[wp1Index], x: draggedX };
            }
          }

          if (wp2Index !== undefined && currentWaypoints[wp2Index]) {
            if (isHorizontal) {
              currentWaypoints[wp2Index] = { ...currentWaypoints[wp2Index], y: draggedY };
            } else {
              currentWaypoints[wp2Index] = { ...currentWaypoints[wp2Index], x: draggedX };
            }
          }

          return {
            ...edge,
            data: {
              ...edge.data,
              waypoints: currentWaypoints,
            },
          };
        })
      );
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      setEdgesToUse((eds) =>
        eds.map((edge) => {
          if (edge.id !== id) return edge;
          const currentWaypoints = edge.data?.waypoints ? [...edge.data.waypoints] : [];
          const cleaned = cleanupRedundantWaypoints(
            currentWaypoints,
            sourceX,
            sourceY,
            sourceStub,
            targetStub,
            targetX,
            targetY
          );
          return {
            ...edge,
            data: {
              ...edge.data,
              waypoints: cleaned,
            },
          };
        })
      );

      window.dispatchEvent(new CustomEvent('unsavedChanges'));
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const corners = useMemo(() => {
    const list = [];
    for (let idx = 1; idx < fullPoints.length - 1; idx++) {
      const pCurr = fullPoints[idx];
      const pPrev = fullPoints[idx - 1];
      const pNext = fullPoints[idx + 1];

      const dx1 = pCurr.x - pPrev.x;
      const dy1 = pCurr.y - pPrev.y;
      const dx2 = pNext.x - pCurr.x;
      const dy2 = pNext.y - pCurr.y;

      const isHorizontal1 = Math.abs(dy1) < 0.01;
      const isVertical1 = Math.abs(dx1) < 0.01;
      const isHorizontal2 = Math.abs(dy2) < 0.01;
      const isVertical2 = Math.abs(dx2) < 0.01;

      const isACorner = (isHorizontal1 && isVertical2) || (isVertical1 && isHorizontal2);

      if (isACorner) {
        list.push({
          x: pCurr.x,
          y: pCurr.y,
          fpIdx: idx,
          waypointIndex: pCurr.waypointIndex,
        });
      }
    }
    return list;
  }, [fullPoints]);

  const onCornerMouseDown = (e, corner) => {
    e.stopPropagation();
    e.preventDefault();

    const fpIdx = corner.fpIdx;
    const promote = new Set([fpIdx, ...outerAutoCornerNeighbors(fpIdx)]);

    const { waypoints: rebuilt, fpIdxToNewIndex } = rebuildWaypoints(promote, null);
    const workingIndex = fpIdxToNewIndex.get(fpIdx);

    setEdgesToUse((eds) =>
      eds.map((edge) =>
        edge.id === id ? { ...edge, data: { ...edge.data, waypoints: rebuilt } } : edge
      )
    );

    window.dispatchEvent(new CustomEvent('unsavedChanges'));

    const handleMouseMove = (moveEvent) => {
      const flowPos = screenToFlowPosition({
        x: moveEvent.clientX,
        y: moveEvent.clientY,
      });

      let draggedX = Math.round(flowPos.x / WAYPOINT_GRID) * WAYPOINT_GRID;
      let draggedY = Math.round(flowPos.y / WAYPOINT_GRID) * WAYPOINT_GRID;

      const snapThreshold = 10;
      if (Math.abs(flowPos.x - sourceX) < snapThreshold) {
        draggedX = sourceX;
      } else if (Math.abs(flowPos.x - targetX) < snapThreshold) {
        draggedX = targetX;
      } else if (Math.abs(flowPos.x - sourceStub.x) < snapThreshold) {
        draggedX = sourceStub.x;
      } else if (Math.abs(flowPos.x - targetStub.x) < snapThreshold) {
        draggedX = targetStub.x;
      } else {
        for (const wp of waypoints) {
          if (Math.abs(flowPos.x - wp.x) < snapThreshold) {
            draggedX = wp.x;
            break;
          }
        }
      }

      if (Math.abs(flowPos.y - sourceY) < snapThreshold) {
        draggedY = sourceY;
      } else if (Math.abs(flowPos.y - targetY) < snapThreshold) {
        draggedY = targetY;
      } else if (Math.abs(flowPos.y - sourceStub.y) < snapThreshold) {
        draggedY = sourceStub.y;
      } else if (Math.abs(flowPos.y - targetStub.y) < snapThreshold) {
        draggedY = targetStub.y;
      } else {
        for (const wp of waypoints) {
          if (Math.abs(flowPos.y - wp.y) < snapThreshold) {
            draggedY = wp.y;
            break;
          }
        }
      }

      setEdgesToUse((eds) =>
        eds.map((edge) => {
          if (edge.id !== id) return edge;
          const currentWaypoints = edge.data?.waypoints ? [...edge.data.waypoints] : [];
          if (currentWaypoints[workingIndex]) {
            currentWaypoints[workingIndex] = { x: draggedX, y: draggedY };
          }
          return {
            ...edge,
            data: {
              ...edge.data,
              waypoints: currentWaypoints,
            },
          };
        })
      );
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      setEdgesToUse((eds) =>
        eds.map((edge) => {
          if (edge.id !== id) return edge;
          const currentWaypoints = edge.data?.waypoints ? [...edge.data.waypoints] : [];
          const cleaned = cleanupRedundantWaypoints(
            currentWaypoints,
            sourceX,
            sourceY,
            sourceStub,
            targetStub,
            targetX,
            targetY
          );
          return {
            ...edge,
            data: {
              ...edge.data,
              waypoints: cleaned,
            },
          };
        })
      );

      window.dispatchEvent(new CustomEvent('unsavedChanges'));
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <>
      <BaseEdge id={id} path={pathString} markerEnd={markerEnd} style={style} />

      {/* Invisible thick line segments for easy hovering and dragging */}
      {!readOnly && !isPreview && (
        <g className="edge-segments-hover">
          {fullPoints.map((p, idx) => {
            if (idx === fullPoints.length - 1) return null;
            const p1 = p;
            const p2 = fullPoints[idx + 1];

            const isHorizontal = Math.abs(p2.y - p1.y) < 0.01;
            const isVertical = Math.abs(p2.x - p1.x) < 0.01;
            const cursor = isHorizontal ? 'ns-resize' : isVertical ? 'ew-resize' : 'move';

            return (
              <line
                key={`seg-hover-${idx}`}
                x1={p1.x}
                y1={p1.y}
                x2={p2.x}
                y2={p2.y}
                stroke="transparent"
                strokeWidth={12}
                style={{ cursor, pointerEvents: 'stroke' }}
                onMouseEnter={(e) => {
                  e.target.setAttribute('stroke', 'rgba(0, 32, 96, 0.15)');
                }}
                onMouseLeave={(e) => {
                  e.target.setAttribute('stroke', 'transparent');
                }}
                onMouseDown={(e) => onSegmentMouseDown(e, idx)}
                onDoubleClick={(e) => onSegmentDoubleClick(e, idx)}
              />
            );
          })}
        </g>
      )}

      {/* Handles and UI controls */}
      {selected && !readOnly && !isPreview && (
        <g className="waypoint-handles">
          {/* Endpoint markers */}
          <circle
            cx={sourceX}
            cy={sourceY}
            r={4.5}
            fill="#002060"
            opacity={0.6}
            pointerEvents="none"
          />
          <circle
            cx={targetX}
            cy={targetY}
            r={4.5}
            fill="#002060"
            opacity={0.6}
            pointerEvents="none"
          />

          {/* Auto-corners: bend points not yet promoted to real waypoints */}
          {corners
            .filter((c) => c.waypointIndex < 0)
            .map((c, idx) => (
              <g
                key={`auto-corner-${idx}`}
                onMouseDown={(e) => onCornerMouseDown(e, c)}
                style={{ cursor: 'move' }}
              >
                <circle cx={c.x} cy={c.y} r={6} fill="transparent" />
                <circle cx={c.x} cy={c.y} r={6} fill="#002060" stroke="#ffffff" strokeWidth={1.5} />
              </g>
            ))}

          {/* Real waypoints: always shown, even before they form a visible bend */}
          {waypoints.map((wp, wIdx) => (
            <g
              key={`waypoint-${wIdx}`}
              onMouseDown={(e) => onWaypointMouseDown(e, wIdx)}
              onDoubleClick={(e) => onWaypointDoubleClick(e, wIdx)}
              style={{ cursor: 'move' }}
            >
              <circle cx={wp.x} cy={wp.y} r={6} fill="transparent" />
              <circle cx={wp.x} cy={wp.y} r={6} fill="#002060" stroke="#ffffff" strokeWidth={1.5} />
            </g>
          ))}
        </g>
      )}
    </>
  );
}
